// ============================================================
// REACT WEBPAGE TEMPLATE — Beginner Friendly & Customizable
// ============================================================

import { useState } from "react";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { Routes, Route } from "react-router-dom";
import Battleship from "./Battleship";
import { Link } from "react-router-dom";
import BattleshipPlay from "./BattleshipPlay";

// ============================================================
// 🎨 STYLES
// ============================================================
const styles = {
  page: {
    fontFamily: "'Georgia', serif",
    backgroundColor: "#f9f6f1",
    color: "#2c2c2c",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
  },
  navbar: {
    backgroundColor: "#2c2c2c",
    color: "#f9f6f1",
    padding: "16px 40px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navLogo: {
    fontSize: "22px",
    fontWeight: "bold",
    letterSpacing: "2px",
    cursor: "pointer",
  },
  navLinks: {
    display: "flex",
    gap: "28px",
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  navLink: {
    color: "#f9f6f1",
    textDecoration: "none",
    fontSize: "15px",
    cursor: "pointer",
    opacity: 0.85,
  },
  hero: {
    textAlign: "center",
    padding: "100px 40px 80px",
    backgroundColor: "#e8e0d5",
    borderBottom: "3px solid #2c2c2c",
  },
  heroTitle: {
    fontSize: "52px",
    fontWeight: "bold",
    marginBottom: "16px",
    lineHeight: 1.2,
  },
  heroSubtitle: {
    fontSize: "20px",
    color: "#555",
    marginBottom: "36px",
    maxWidth: "750px",
    margin: "0 auto 36px",
  },
  heroButton: {
    backgroundColor: "#2c2c2c",
    color: "#f9f6f1",
    border: "none",
    padding: "14px 36px",
    fontSize: "16px",
    borderRadius: "4px",
    cursor: "pointer",
    letterSpacing: "1px",
  },
  cardsSection: {
    padding: "72px 40px",
    maxWidth: "1265px",
    margin: "0 auto",
  },
  sectionTitle: {
    fontSize: "30px",
    fontWeight: "bold",
    marginBottom: "12px",
    textAlign: "center",
  },
  sectionHint: {
    textAlign: "center",
    color: "#888",
    fontSize: "14px",
    marginBottom: "40px",
    fontStyle: "italic",
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "28px",
    alignItems: "stretch",
  },

  // --- Base card style ---
  card: {
    backgroundColor: "#fff",
    border: "2px solid #2c2c2c",
    borderRadius: "6px",
    padding: "32px 28px",
    boxShadow: "4px 4px 0px #2c2c2c",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    position: "relative",
    zIndex: 0,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },

  // --- Card style when hovered ---
  cardHovered: {
    transform: "translateY(-6px)",
    boxShadow: "6px 8px 0px #2c2c2c",
  },

  cardIcon: {
    fontSize: "32px",
    marginBottom: "14px",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: "10px",
  },

  // --- Each bullet line on the card face ---
  cardLine: {
    fontSize: "15px",
    color: "#555",
    lineHeight: 1.6,
    margin: 0,
  },

  // --- "Click to expand" hint at the bottom of the card ---
  cardClickHint: {
    marginTop: "auto",
    paddingTop: "16px",
    fontSize: "12px",
    color: "#aaa",
    fontStyle: "italic",
  },

  // ============================================================
  // MODAL STYLES
  // ============================================================
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    boxSizing: "border-box",
  },
  modalBox: {
    backgroundColor: "#fff",
    border: "2px solid #2c2c2c",
    borderRadius: "8px",
    boxShadow: "8px 8px 0px #2c2c2c",
    width: "100%",
    maxWidth: "860px",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: "48px 52px",
    position: "relative",
    boxSizing: "border-box",
  },
  modalClose: {
    position: "absolute",
    top: "16px",
    right: "20px",
    fontSize: "26px",
    cursor: "pointer",
    background: "none",
    border: "none",
    color: "#2c2c2c",
    lineHeight: 1,
    fontWeight: "bold",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "2px solid #2c2c2c",
  },
  modalIcon: {
    fontSize: "40px",
  },
  modalTitle: {
    fontSize: "28px",
    fontWeight: "bold",
    margin: 0,
  },
  modalSummary: {
    fontSize: "15px",
    color: "#555",
    lineHeight: 1.7,
    marginBottom: "32px",
  },
  modalEntry: {
    marginBottom: "32px",
    paddingBottom: "28px",
    borderBottom: "1px solid #e8e0d5",
  },
  modalEntryTitle: {
    fontSize: "17px",
    fontWeight: "bold",
    marginBottom: "4px",
    color: "#2c2c2c",
  },
  modalEntrySubtitle: {
    fontSize: "14px",
    color: "#888",
    marginBottom: "8px",
    fontStyle: "italic",
  },
  // --- Description: plain prose paragraph below the subtitle ---
  modalEntryDescription: {
    fontSize: "14px",
    color: "#555",
    lineHeight: 1.7,
    marginBottom: "10px",
  },
  // --- Each bullet line inside a modal entry ---
  modalEntryLine: {
    fontSize: "14px",
    color: "#444",
    lineHeight: 1.75,
    margin: 0,
  },

  contactSection: {
    backgroundColor: "#2c2c2c",
    color: "#f9f6f1",
    padding: "72px 40px",
    textAlign: "center",
  },
  footer: {
    backgroundColor: "#1a1a1a",
    color: "#888",
    textAlign: "center",
    padding: "24px 40px",
    fontSize: "14px",
  },
};

// ============================================================
// 📦 DATA — Edit this to change your page's content
// ============================================================

const SITE_NAME = "JDR Resume";
const NAV_LINKS = ["Home", "About", "Work", "Contact"];

const HERO = {
  title: "Hello, I'm Jonathan D. Roberts",
  subtitle: "Passionate computer engineer and software developer with hands-on experience in embedded systems, full-stack development, and hardware-software integration.",
  buttonText: "Get in Touch",
};

// ============================================================
// 📋 CARDS DATA
//
// Each card has:
//   icon   — symbol shown on the card
//   title  — card heading
//   text   — array of strings shown as bullet lines on the card face.
//              Use "" (empty string) for a blank gap between groups.
//   modal  — the full content shown when the card is clicked:
//     summary  — a paragraph at the top of the modal
//     entries  — a list of items (jobs, degrees, projects, etc.)
//       Each entry has:
//         title       — e.g. job title or degree name
//         subtitle    — e.g. company name + duration (shown in italics)
//         description — (optional) plain prose paragraph below the subtitle,
//                       no bullet point. Leave out or set to "" to skip it.
//         text        — array of bullet-point strings.
//                       Use "" (empty string) for a blank gap between groups.
//
// To add a new entry, copy one of the { title, subtitle, description, text }
// blocks and paste it into the entries array with your own content.
// ============================================================
const CARDS = [
  {
    icon: "✦",
    title: "Education",
    text: [
      "Master of Science in Computer Science at the College of Staten Island",
      "",
      "Bachelor of Science in Computer Engineering at SUNY New Paltz",
      "",
      "Software Engineering Training with Revature LLC",
    ],
    modal: {
      summary: "My academic background includes a combination of Hardware and Software, with a focus on building practical skills in Application Development, Web Development and Hardware Design.",
      entries: [
        {
          title: "M.S. Computer Science",
          subtitle: "College of Staten Island — In Progress (Expected May 2027)",
          description: "",
          text: [
            "Focus: AI/Machine Learning & Cyber Security",
            "Relevant coursework: Software Engineering, Artificial Intelligence & Knowledge Engineering, Algorithms & Data Structures, Cryptography",
            "Current GPA: 4.0",
          ],
        },
        {
          title: "B.S. Computer Engineering",
          subtitle: "SUNY New Paltz — Graduated 2023",
          description: "",
          text: [
            "Relevant coursework: Software Engineering, Embedded Systems, Operating Systems, Data Structures",
            "Volunteer tutored for Computer Science: Data Structures & Discrete and Continuous Algorithms",
            "GPA: 3.1",
          ],
        },
        {
          title: "Software Engineering Training",
          subtitle: "Revature LLC — Completed 2025",
          description: "",
          text: [
            "Completed training in backend application development using Java and SQL, with hands-on experience in frameworks such as Javalin and Spring, along with unit testing using Mockito",
            "Gained practical experience in dependency injection, secure coding practices including SQL injection mitigation, and designing RESTful APIs with proper endpoint configuration and request handling",
          ],
        },
        // ↓ To add another degree or certification, copy the block above and paste it here
      ],
    },
  },
  {
    icon: "◈",
    title: "Work Experience",
    text: [
      "IT Help Desk at CSI",
      "",
      "Validation Engineer at Stellantis",
      "",
      "Restaurant Server at Skinflints Bar & Grill",
      "",
      "Teaching Assistant at IS96",
      "",
      "Lifeguard at the FIAO Community Center",
    ],
    modal: {
      summary: "A mix of technical and customer-facing roles that have built both my engineering skills and my ability to communicate clearly under pressure.",
      entries: [
        {
          title: "IT Help Desk",
          subtitle: "College of Staten Island — Current",
          description: "",
          text: [
            "Provide technical support to students and faculty",
            "Troubleshoot hardware, software, and network issues",
            "Communicate quickly, effectively and professionally at all times",
          ],
        },
        {
          title: "Validation Engineer",
          subtitle: "Stellantis — 11 months",
          description: "",
          text: [
            "Tested embedded automotive software against safety specifications",
            "Validated Electronic Control Units (ECUs) for unreleased electric vehicles",
            "Collaborated with cross-disciplinary engineering teams",
          ],
        },
        {
          title: "Restaurant Server",
          subtitle: "Skinflint Bar & Restaurant — 18 months",
          description: "",
          text: [
            "Managed high-volume service in a fast-paced environment",
            "Developed strong communication and teamwork skills",
          ],
        },
        {
          title: "Pharmacy Technician/Cashier",
          subtitle: "TOPS Pharmacy — 5 months",
          description: "",
          text: [
            "Assisted customers in acquiring their medications",
            "Filled medication bottles to prescribed dosage levels",
            "Trained and abided by HIPAA privacy and security measures",
          ],
        },
        {
          title: "Teaching Assistant",
          subtitle: "IS96 Summer School Program — 4 Months",
          description: "",
          text: [
            "Supervised and chaperoned students from kindergarten through eighth grade throughout a structured summer school program",
            "Assisted classroom teachers in managing student engagement and maintaining an orderly, productive learning environment",
          ],
        },
        {
          title: "Lifeguard",
          subtitle: "FIAO Community Center — 4 Months",
          description: "",
          text: [
            "Certified by the American Red Cross in lifeguarding, first aid, and water safety",
            "Prepared the pool and locker room facilities daily for patron use, including setup and end-of-day closure procedures",
            "Monitored aquatic activities to ensure patron safety, responding assertively and professionally to incidents and rule violations",
          ],
        },
        // ↓ To add another job, copy the block above and paste it here
      ],
    },
  },
  {
    icon: "❋",
    title: "Projects",
    text: [
      "Battleship Video Game",
      "",
      "AI Training Loan Prediction",
      "",
      "Self Driving Car with Cruise Control",
      "",
      "FPGA Video Game",
      "",
      "Social Media Messenger App",
    ],
    modal: {
      summary: "Personal and academic projects spanning embedded systems, hardware design, machine learning, and full-stack development — applying coursework concepts to real, functional software and hardware.",
      entries: [
        {
          title: "Battleship Video Game",
          subtitle: "Personal Project — Python, Pygame, Full-Stack, Socket Programming, Server-Client Architecture",
          description: "A full-stack digital reimagining of the classic Battleship board game, built from the ground up with a custom networking layer and original gameplay features including power-ups and animated visuals.",
          link: "/battleship",
          text: [
            "Developed using Python and Pygame with a custom-built client-server architecture communicating via JSON message passing",
            "",
            "Local multiplayer mode supports real-time two-player gameplay over a network",
            "Single-player mode features an AI opponent with selectable difficulty levels",
            "Added unique mechanics including power-ups and per-turn countdown timers for increased competitive pressure",
            "Incorporated custom hand-drawn sprite animations for an original visual style",
          ],
        },
        {
          title: "AI Loan Prediction Model",
          subtitle: "Kaggle Competition — Python, Machine Learning, Gradient Boosting, Ensemble Methods, Predictive Analytics",
          description: "A competitive machine learning project focused on building and optimizing a predictive classification model to assess the likelihood of borrower loan repayment using a large synthetic financial dataset.",
          text: [
            "Developed an ensemble of gradient-boosting models combining CatBoost, LightGBM, and XGBoost",
            "Applied 5-fold cross-validation for hyperparameter tuning and combined predictions via weighted averaging",
            "Enhanced feature quality through iterative feature engineering guided by importance analysis",
            "Achieved 92.5% classification accuracy, placing in the top 23% of 3,700 competition entries",
          ],
        },
        {
          title: "Social Media Messenger Application",
          subtitle: "Academic Project — Java, SQL, Spring Boot, RESTful API Development, Dependency Injection, Unit Testing",
          description: "A backend system designed to simulate the core infrastructure of a scalable social media messaging platform, with a focus on clean architectural layering and secure API design.",
          text: [
            "Engineered a RESTful back-end supporting user registration, authentication, and bidirectional messaging",
            "Leveraged Spring Boot for dependency injection and SQL for relational database design covering accounts and messages",
            "Implemented a full three-tier architecture: data access object (DAO), service, and controller layers",
            "Validated all endpoints using Mockito-based unit tests and applied input sanitization to prevent SQL injection",
          ],
        },
        {
          title: "Video Game on an Intel/Altera FPGA",
          subtitle: "Academic Project — Verilog HDL, Intel/Altera FPGA, Quartus Prime, FSM Design, Digital Logic",
          description: "A real-time embedded game system designed and synthesized entirely in hardware, demonstrating proficiency in digital logic design, finite state machine architecture, and FPGA development workflows.",
          text: [
            "Designed and implemented the full game system on an Intel/Altera FPGA using Verilog HDL with integrated I/O hardware, developed in Quartus Prime",
            "Architected a modular FSM-based control system managing gameplay logic including enemy spawning, collision detection, game state transitions, and pseudo-random laser generation",
            "Verified correctness through simulation and on-board hardware testing, ensuring reliable timing behavior and accurate signal propagation across all modules",
          ],
        },
        {
          title: "Self-Driving Car with Cruise Control",
          subtitle: "Academic Project — ARM STM32, C/C++, Embedded Systems, Sensor Signal Processing, PWM Motor Control",
          description: "An autonomous embedded vehicle system built on an ARM STM32 microcontroller, integrating multi-sensor feedback, closed-loop speed regulation, and programmable driving behaviors through a real-time control architecture.",
          text: [
            "Designed and implemented a multi-sensor integration layer incorporating directional microphone, light, and heat sensors for dynamic environment tracking",
            "Developed a closed-loop cruise control system that monitored wheel RPM via photogate sensors and regulated motor output to maintain constant speed under variable resistance",
            "Programmed multiple autonomous driving patterns — including figure-8, snake, and reverse — using PWM-controlled DC motors and servo steering managed through precise timing logic",
            "Built an interactive LCD interface supporting mode selection, real-time RPM display, and system reset for intuitive operator control and field testing",
          ],
        },
        // ↓ To add another project, copy the block above and paste it here
      ],
    },
  },
];

const CONTACT = {
  title: "Contacts & Links",
  subtitle: "Questions? Please reach out with any of the following methods.",
};

const FOOTER_TEXT = `© ${new Date().getFullYear()} ${SITE_NAME}. All rights reserved.`;


// ============================================================
// 🧩 BULLET LIST RENDERER
// A small helper that takes an array of strings and renders them
// as bullet points. Empty strings ("") become blank spacer rows.
// Used in both the Card and Modal components below.
// ============================================================
function BulletList({ lines, lineStyle, spacerHeight = "8px" }) {
  return (
    <div>
      {lines.map((line, i) =>
        line === ""
          ? <div key={i} style={{ height: spacerHeight }} />
          : <p key={i} style={lineStyle}>• {line}</p>
      )}
    </div>
  );
}


// ============================================================
// 🧩 MODAL COMPONENT
// ============================================================
function Modal({ card, onClose }) {
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>

        <button style={styles.modalClose} onClick={onClose}>✕</button>

        <div style={styles.modalHeader}>
          <span style={styles.modalIcon}>{card.icon}</span>
          <h2 style={styles.modalTitle}>{card.title}</h2>
        </div>

        <p style={styles.modalSummary}>{card.modal.summary}</p>

        {card.modal.entries.map((entry, index) => (
          <div
            key={index}
            style={
              index === card.modal.entries.length - 1
                ? { ...styles.modalEntry, borderBottom: "none", marginBottom: 0, paddingBottom: 0 }
                : styles.modalEntry
            }
          >
            <div style={styles.modalEntryTitle}>{entry.title}</div>
            <div style={styles.modalEntrySubtitle}>{entry.subtitle}</div>

            {/* Description: only rendered if the field exists and isn't empty */}
            {entry.description && entry.description !== "" && (
              <p style={styles.modalEntryDescription}>{entry.description}</p>
            )}

            {/* Bullet list from the text array */}
            <BulletList lines={entry.text} lineStyle={styles.modalEntryLine} />
            {entry.link && (
              <Link
                to={entry.link}
                style={{
                  display: "inline-block",
                  marginTop: "14px",
                  padding: "8px 20px",
                  backgroundColor: "#2c2c2c",
                  color: "#f9f6f1",
                  borderRadius: "4px",
                  textDecoration: "none",
                  fontSize: "13px",
                  letterSpacing: "1px",
                }}
              >
                View Project →
              </Link>
)}
          </div>
        ))}

      </div>
    </div>
  );
}


// ============================================================
// 🧩 CARD COMPONENT
// ============================================================
function Card({ card, onClick }) {
  const [hovered, setHovered] = useState(false);

  const cardStyle = hovered
    ? { ...styles.card, ...styles.cardHovered }
    : styles.card;

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.cardIcon}>{card.icon}</div>
      <h3 style={styles.cardTitle}>{card.title}</h3>

      {/* Bullet list from the text array */}
      <BulletList lines={card.text} lineStyle={styles.cardLine} spacerHeight="6px" />

      <p style={styles.cardClickHint}>Click to expand ↗</p>
    </div>
  );
}

// ============================================================
// 🧩 HOME PAGE
// ============================================================
function Home() {
  const [openCard, setOpenCard] = useState(null);

  function scrollToContact() {
    document.getElementById("contact").scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div style={styles.page}>

      {openCard && (
        <Modal card={openCard} onClose={() => setOpenCard(null)} />
      )}

      {/* ── NAVBAR ─────────────────────────────────── */}
      <nav style={styles.navbar}>
        <div style={styles.navLogo}>{SITE_NAME}</div>
        <ul style={styles.navLinks}>
          {NAV_LINKS.map((link) => (
            <li key={link}>
              <a style={styles.navLink} href={`#${link.toLowerCase()}`}>
                {link}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* ── HERO SECTION ───────────────────────────── */}
      <section id="home" style={styles.hero}>
        <h1 style={styles.heroTitle}>{HERO.title}</h1>
        <p style={styles.heroSubtitle}>{HERO.subtitle}</p>
        <button style={styles.heroButton} onClick={scrollToContact}>
          {HERO.buttonText}
        </button>
      </section>

      {/* ── CARDS SECTION ──────────────────────────── */}
      <section id="about" style={styles.cardsSection}>
        <h2 style={styles.sectionTitle}>About Me</h2>
        <p style={styles.sectionHint}>Click a card to learn more</p>

        <div style={styles.cardsGrid}>
          {CARDS.map((card) => (
            <Card
              key={card.title}
              card={card}
              onClick={() => setOpenCard(card)}
            />
          ))}
        </div>
      </section>

      {/* ── CONTACT SECTION ────────────────────────── */}
      <section id="contact" style={styles.contactSection}>
        <h2 style={styles.sectionTitle}>{CONTACT.title}</h2>
        <p>{CONTACT.subtitle}</p>
        <ul style={{ listStyle: "none", padding: 0, marginTop: "32px", lineHeight: 2.2, fontSize: "17px" }}>
          <li>📧 <a href="mailto:jonathanroberts5423@gmail.com" style={{ color: "#f9f6f1" }}>JonathanRoberts5423@gmail.com</a></li>
          <li><FaLinkedin size={15} /> <a href="https://linkedin.com/in/jonathan-dr" target="_blank" style={{ color: "#f9f6f1" }}>Jonathan-DR</a></li>
          <li><FaGithub size={15} /> <a href="https://github.com/jonathandakrob" target="_blank" style={{ color: "#f9f6f1" }}>JonathanDakRob</a></li>
          <li>📱 +1 (917) 863-4980</li>
        </ul>
      </section>

      {/* ── FOOTER ─────────────────────────────────── */}
      <footer style={styles.footer}>
        <p>{FOOTER_TEXT}</p>
      </footer>

    </div>
  )
  
}


// ============================================================
// 🧩 MAIN COMPONENT
// ============================================================
export default function App() {
  return (
    <Routes>
      <Route path="/" element={ <Home /> } />
      <Route path="/battleship" element={ <Battleship /> } />
      <Route path="/battleship/play" element={<BattleshipPlay />} />      
    </Routes>
  );
}